// Outbox hlasovek v IndexedDB: nahrávka se nejdřív uloží, pak se nahraje. Přežije offline i zavření aplikace.
// Idempotence: `id` je noteId, server stejnou hlasovku podruhé nepřijme jako novou.
// Vlastník: každá položka patří uživateli a firmě, pod kterými vznikla. Odešle se jen pod nimi.
import { createStore, del, get, keys, set, type UseStore } from 'idb-keyval'

/** Kdo a v které firmě právě pracuje (z `useAuthStore().me`). */
export interface OutboxOwner {
  userId: string
  orgId: string
}

export interface OutboxItem {
  /** uuid hlasovky (noteId, idempotenční klíč). */
  id: string
  /** Uživatel, který hlasovku nahrál. Položka bez vlastníka (starší verze) se nikdy neodešle. */
  userId: string
  /** Firma, ve které vznikla. */
  orgId: string
  blob: Blob
  /** Typ bez parametrů. */
  mime: string
  durationMs: number
  /** Text z Web Speech, prázdný řetězec když chybí. */
  deviceText: string
  createdAt: number
  clientId?: string
  jobId?: string
  attempts: number
  lastError?: string
}

export type NewOutboxItem = Omit<OutboxItem, 'attempts' | 'createdAt'> & { createdAt?: number }

/** Hlasovka, kterou server odmítl natrvalo (nebo se vyčerpal počet pokusů): už se nezopakuje. */
export interface FailedNote {
  id: string
  status: number
  message: string
  durationMs: number
  deviceText: string
}

export interface FlushResult {
  /** Id úspěšně nahraných hlasovek. */
  uploaded: string[]
  /** Hlasovky odmítnuté natrvalo. */
  failed: FailedNote[]
  /** Kolik jich zůstalo ve frontě pro aktuálního uživatele a firmu. */
  remaining: number
}

export interface OutboxStorage {
  get(id: string): Promise<OutboxItem | undefined>
  set(id: string, item: OutboxItem): Promise<void>
  del(id: string): Promise<void>
  keys(): Promise<string[]>
}

export const MAX_ATTEMPTS = 20

/** Úložiště v paměti: záloha, když prohlížeč IndexedDB nemá, a adaptér pro testy. */
export function createMemoryOutboxStorage(): OutboxStorage {
  const map = new Map<string, OutboxItem>()
  return {
    get: async (id) => map.get(id),
    set: async (id, item) => void map.set(id, item),
    del: async (id) => void map.delete(id),
    keys: async () => [...map.keys()],
  }
}

function idbStorage(): OutboxStorage {
  let store: UseStore | null = null
  const s = () => (store ??= createStore('wac-buddy', 'voice-outbox'))
  return {
    get: (id) => get<OutboxItem>(id, s()),
    set: (id, item) => set(id, item, s()),
    del: (id) => del(id, s()),
    keys: async () => (await keys(s())).map(String),
  }
}

let storage: OutboxStorage | null = null
const failedNotes: FailedNote[] = []

function getStorage(): OutboxStorage {
  storage ??= typeof indexedDB === 'undefined' ? createMemoryOutboxStorage() : idbStorage()
  return storage
}

/** Pro testy: vlastní úložiště (null = výchozí). Maže i seznam odmítnutých. */
export function setOutboxStorage(next: OutboxStorage | null): void {
  storage = next
  failedNotes.length = 0
}

export async function enqueue(item: NewOutboxItem): Promise<void> {
  await getStorage().set(item.id, { ...item, createdAt: item.createdAt ?? Date.now(), attempts: 0 })
}

/** Všechny položky od nejstarší. */
export async function list(): Promise<OutboxItem[]> {
  const st = getStorage()
  const items = await Promise.all((await st.keys()).map((id) => st.get(id)))
  return items
    .filter((i): i is OutboxItem => i !== undefined)
    .sort((a, b) => a.createdAt - b.createdAt)
}

export async function count(): Promise<number> {
  return (await getStorage().keys()).length
}

/** Kolik položek čeká na odeslání pod daným vlastníkem (bez vlastníka = 0). */
export async function countFor(owner: OutboxOwner | null): Promise<number> {
  if (!owner) return 0
  return (await list()).filter((i) => i.userId === owner.userId && i.orgId === owner.orgId).length
}

export async function remove(id: string): Promise<void> {
  await getStorage().del(id)
}

/** Odmítnuté hlasovky od startu aplikace (v paměti). */
export function getFailed(): readonly FailedNote[] {
  return failedNotes
}

export function clearFailed(): void {
  failedNotes.length = 0
}

function statusOf(e: unknown): number {
  const status =
    typeof e === 'object' && e !== null ? (e as { status?: unknown }).status : undefined
  return typeof status === 'number' ? status : 0
}

function messageOf(e: unknown): string {
  return e instanceof Error ? e.message : String(e)
}

/** 4xx kromě 401 (přihlášení vypršelo) a 429 (limit) je trvalé odmítnutí. */
function isPermanent(status: number): boolean {
  return status >= 400 && status < 500 && status !== 401 && status !== 429
}

function toFailed(item: OutboxItem, status: number, message: string): FailedNote {
  return { id: item.id, status, message, durationMs: item.durationMs, deviceText: item.deviceText }
}

let running: Promise<FlushResult> | null = null
let rerun = false

async function runFlush(
  upload: (item: OutboxItem) => Promise<unknown>,
  currentOwner: () => OutboxOwner | null,
): Promise<FlushResult> {
  const result: FlushResult = { uploaded: [], failed: [], remaining: 0 }
  const st = getStorage()
  let blocked = false
  do {
    rerun = false
    for (const item of await list()) {
      // Vlastník se čte při každé položce: přepnutí uživatele za běhu nesmí nic poslat pod cizím účtem.
      const owner = currentOwner()
      if (!owner) {
        // Nikdo není přihlášen: nic neposíláme a nic nemažeme.
        blocked = true
        break
      }
      // Cizí uživatel: nikdy se neodešle pod jiným účtem, proto se smaže.
      if (item.userId !== owner.userId) {
        await remove(item.id)
        continue
      }
      // Stejný uživatel, jiná firma: počká, až se přepne zpět.
      if (item.orgId !== owner.orgId) continue
      if (item.attempts >= MAX_ATTEMPTS) {
        await remove(item.id)
        result.failed.push(toFailed(item, 0, item.lastError ?? 'Příliš mnoho pokusů.'))
        continue
      }
      try {
        await upload(item)
        await remove(item.id)
        result.uploaded.push(item.id)
      } catch (e) {
        const status = statusOf(e)
        if (isPermanent(status)) {
          await remove(item.id)
          result.failed.push(toFailed(item, status, messageOf(e)))
        } else {
          // 401, 429, 5xx a síť: necháme na příště a dál nezkoušíme (spadlo by to stejně).
          await st.set(item.id, { ...item, attempts: item.attempts + 1, lastError: messageOf(e) })
          blocked = true
          break
        }
      }
    }
  } while (rerun && !blocked)
  failedNotes.push(...result.failed)
  result.remaining = await countFor(currentOwner())
  return result
}

/**
 * Postupně nahraje frontu jen za aktuálního uživatele a firmu (`owner`): položky cizího uživatele
 * smaže, položky jiné firmy přeskočí. 2xx = hotovo, trvalé 4xx = odmítnuto (zapsáno do `getFailed()`),
 * jinak zůstává a roste `attempts` (po MAX_ATTEMPTS se vzdá). Souběžná volání sdílí jeden běh.
 */
export function flush(options: {
  upload: (item: OutboxItem) => Promise<unknown>
  /** Aktuální uživatel a firma; null = nikdo není přihlášen. */
  owner: () => OutboxOwner | null
}): Promise<FlushResult> {
  if (running) {
    rerun = true
    return running
  }
  running = runFlush(options.upload, options.owner).finally(() => {
    running = null
  })
  return running
}
